import { useEffect, useState } from 'react';
import { useAuth } from '../auth';
import PageHeader from '../components/PageHeader';
import PushButton from '../components/PushButton';
import AdminPanel from '../components/AdminPanel';
import NotifPrefsPanel from '../components/NotifPrefsPanel';
import InstallPanel from '../components/InstallPanel';
import { toast } from '../toast';
import { useTheme } from '../theme';

const HAPTIC_KEY = 'wcfantasy.haptic';
const SOUND_KEY = 'wcfantasy.sound';
const TOAST_KEY = 'wcfantasy.toasts';
type ToastMode = 'all' | 'errors-only' | 'off';

export default function Settings() {
  const { user, signOut } = useAuth();
  const [theme, setTheme] = useTheme();
  const [hapticOn, setHapticOn] = useState(() => {
    try { return localStorage.getItem(HAPTIC_KEY) !== 'off'; } catch { return true; }
  });
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem(SOUND_KEY) === 'on'; } catch { return false; }
  });
  const [toastMode, setToastMode] = useState<ToastMode>(() => {
    try { const v = localStorage.getItem(TOAST_KEY); return v === 'off' || v === 'errors-only' ? v : 'all'; } catch { return 'all'; }
  });

  useEffect(() => { try { localStorage.setItem(HAPTIC_KEY, hapticOn ? 'on' : 'off'); } catch {} }, [hapticOn]);
  useEffect(() => { try { localStorage.setItem(SOUND_KEY, soundOn ? 'on' : 'off'); } catch {} }, [soundOn]);
  useEffect(() => {
    try {
      if (toastMode === 'all') localStorage.removeItem(TOAST_KEY);
      else localStorage.setItem(TOAST_KEY, toastMode);
    } catch {}
  }, [toastMode]);

  function clearPWA() {
    try { localStorage.removeItem('wcfantasy.pwa-dismissed'); } catch {}
    toast.success('PWA install banner reset. Refresh to see it.');
  }

  function clearCache() {
    try {
      const keys = Object.keys(localStorage);
      for (const k of keys) if (k.startsWith('wcfantasy.')) localStorage.removeItem(k);
      toast.success('Local preferences cleared.');
    } catch {}
  }

  return (
    <>
      <PageHeader title="Settings" />
      <div className="px-4 lg:px-0 space-y-3">
        <PushButton />

        {user && <NotifPrefsPanel />}

        <InstallPanel />

        <section className="card p-3 space-y-2">
          <h2 className="text-xs uppercase tracking-widest text-dim mb-1">Appearance</h2>
          <div className="grid grid-cols-2 gap-1.5">
            <ThemeBtn label="Dark" active={theme === 'dark'} onClick={() => setTheme('dark')} preview="dark" />
            <ThemeBtn label="Light" active={theme === 'light'} onClick={() => setTheme('light')} preview="light" />
          </div>
        </section>

        <section className="card p-3 space-y-2">
          <h2 className="text-xs uppercase tracking-widest text-dim mb-1">Feedback</h2>
          <Row label="Haptic feedback" desc="Buzz on bets, captain picks, reactions." value={hapticOn} onChange={setHapticOn} />
          <Row label="Sound effects (coming soon)" desc="Audio cues on goals." value={soundOn} onChange={setSoundOn} />
          <div className="pt-1">
            <div className="text-sm">Toast messages</div>
            <div className="text-[10px] text-dim mb-1.5">Pop-ups for confirmations, errors, etc.</div>
            <div className="grid grid-cols-3 gap-1.5">
              <ToastBtn label="All" active={toastMode === 'all'} onClick={() => setToastMode('all')} />
              <ToastBtn label="Errors only" active={toastMode === 'errors-only'} onClick={() => setToastMode('errors-only')} />
              <ToastBtn label="Off" active={toastMode === 'off'} onClick={() => setToastMode('off')} />
            </div>
          </div>
        </section>

        <section className="card p-3 space-y-2">
          <h2 className="text-xs uppercase tracking-widest text-dim mb-1">Keyboard shortcuts (desktop)</h2>
          <ul className="text-xs text-dim space-y-0.5 font-mono">
            <li><kbd>/</kbd>  open search</li>
            <li><kbd>g</kbd> <kbd>s</kbd>  go to standings</li>
            <li><kbd>g</kbd> <kbd>a</kbd>  go to activity</li>
            <li><kbd>g</kbd> <kbd>b</kbd>  go to bets</li>
            <li><kbd>g</kbd> <kbd>n</kbd>  go to news</li>
            <li><kbd>Esc</kbd>  close modal</li>
          </ul>
        </section>

        <section className="card p-3 space-y-2">
          <h2 className="text-xs uppercase tracking-widest text-dim mb-1">Account</h2>
          {user ? (
            <>
              <div className="text-sm">Signed in as <span className="font-semibold">{user}</span></div>
              <button onClick={signOut} className="h-9 px-3 rounded-lg bg-ink-800 text-chalk text-xs">Sign out</button>
            </>
          ) : (
            <div className="text-xs text-dim">Not signed in.</div>
          )}
        </section>

        <AdminPanel />

        <section className="card p-3 space-y-2">
          <h2 className="text-xs uppercase tracking-widest text-dim mb-1">Maintenance</h2>
          <div className="flex gap-2 flex-wrap">
            <button onClick={clearPWA} className="h-8 px-3 rounded-lg bg-ink-800 text-chalk text-xs">Reset PWA banner</button>
            <button onClick={clearCache} className="h-8 px-3 rounded-lg bg-ink-800 text-chalk text-xs">Clear local prefs</button>
          </div>
        </section>
      </div>
    </>
  );
}

function ToastBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      className={`py-1.5 text-xs rounded-md ${active ? 'bg-pitch-500/20 text-pitch-400 ring-1 ring-pitch-500/40' : 'bg-ink-800 text-chalk'}`}
    >{label}</button>
  );
}

function ThemeBtn({ label, active, onClick, preview }: { label: string; active: boolean; onClick: () => void; preview: 'dark' | 'light' }) {
  const swatch = preview === 'dark'
    ? { bg: '#0d0f14', fg: '#f4f5f7', accent: '#3fd66a', border: '#1c2230' }
    : { bg: '#ffffff', fg: '#0d0f14', accent: '#23b350', border: '#d5dae2' };
  return (
    <button
      type="button" onClick={onClick}
      className={`flex items-center gap-2.5 rounded-lg p-2 text-left text-sm transition-colors ${active ? 'bg-pitch-500/15 ring-1 ring-pitch-500/40 text-chalk' : 'bg-ink-800 text-dim hover:bg-ink-700'}`}
    >
      <span
        className="shrink-0 w-10 h-10 rounded-md flex items-center justify-center text-xs font-bold"
        style={{ backgroundColor: swatch.bg, color: swatch.fg, border: `1px solid ${swatch.border}` }}
      >
        <span style={{ color: swatch.accent }}>●</span>
      </span>
      <span className="font-medium">{label}</span>
    </button>
  );
}

function Row({ label, desc, value, onChange }: { label: string; desc?: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
      <div className="flex-1 min-w-0">
        <div className="text-sm">{label}</div>
        {desc && <div className="text-[10px] text-dim">{desc}</div>}
      </div>
      <span
        role="switch" aria-checked={value}
        onClick={() => onChange(!value)}
        className={`shrink-0 inline-flex items-center w-10 h-6 rounded-full p-0.5 transition-colors ${value ? 'bg-pitch-500' : 'bg-ink-700'}`}
      >
        <span className={`block w-5 h-5 rounded-full bg-chalk transition-transform ${value ? 'translate-x-4' : ''}`} />
      </span>
    </label>
  );
}
